<!--
    BD Handover Approval — Wave 3
    Listet PENDING_APPROVAL HandOvers und erlaubt Genehmigen/Ablehnen.
    TM-1000002054: 'Genehmigen' needs the role admin, bd_admin or bd_approver (also for an
    own submission); 'Ablehnen' has no self-lock.
    @ticket TM-1000001786
    @ticket TM-1000002054
-->
@if (initialized) {
    <div class="flex flex-column gap-3">

        <!-- Header -->
        <div class="flex align-items-center gap-2">
            <i class="fa-light fa-user-shield text-primary"></i>
            <span class="font-semibold text-lg">Übergabe genehmigen</span>
        </div>

        <!-- Error -->
        @if (errorMessage) {
            <p-message severity="error" [text]="errorMessage" styleClass="w-full"></p-message>
        }

        <!-- Empty state -->
        @if (rows.length === 0) {
            <div class="surface-card border-round p-4 text-center">
                <i class="fa-light fa-check-double text-3xl text-400 mb-2"></i>
                <p class="m-0 text-600">Keine offenen Übergaben zur Genehmigung vorhanden.</p>
            </div>
        }

        <!-- Rows -->
        @if (rows.length > 0) {
            <div class="flex flex-column gap-3">
                @for (row of rows; track row.id) {
                    <div class="surface-card border-round p-3"
                         [attr.data-hand-over-id]="row.id"
                         [class.bd-row-processed]="row.processed">

                        <div class="flex flex-wrap align-items-start justify-content-between gap-3">

                            <!-- Left: Details -->
                            <div class="flex flex-column gap-1">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-light fa-file-arrow-up text-orange-500"></i>
                                    <span class="font-semibold">
                                        Inkasso-Übergabe #{{ row.id }}
                                    </span>
                                    <!-- C10 AK-3: genau EIN Statusbadge — vor der
                                         Entscheidung "Freigabe ausstehend", danach
                                         ausschliesslich "Genehmigt"/"Abgelehnt". -->
                                    @if (!row.processed) {
                                        <p-tag value="Freigabe ausstehend"
                                               severity="warn"
                                               styleClass="text-xs"></p-tag>
                                    } @else if (row.decision === 'APPROVED') {
                                        <p-tag value="Genehmigt"
                                               severity="success"
                                               styleClass="text-xs"></p-tag>
                                    } @else if (row.decision === 'REJECTED') {
                                        <p-tag value="Abgelehnt"
                                               severity="danger"
                                               styleClass="text-xs"></p-tag>
                                    }
                                </div>

                                <div class="text-sm text-600">
                                    <span class="font-medium">Eingereicht von:</span>
                                    {{ getSubmitterLabel(row.submittedBy) }}
                                    @if (row.submittedAt) {
                                        <span class="ml-2 text-500">
                                            am {{ row.submittedAt | date:'dd.MM.yyyy HH:mm' }}
                                        </span>
                                    }
                                </div>

                                <!-- C10 AK-7: Genehmiger (aktueller Benutzer) anzeigen -->
                                <div class="text-sm text-600">
                                    <span class="font-medium">Freigabe durch:</span>
                                    {{ currentUserName ?? 'aktueller Benutzer' }}
                                </div>

                                <div class="text-sm text-600">
                                    <span class="font-medium">Aktion:</span> {{ getActionLabel(row.action) }}
                                    @if (row.contractId != null) {
                                        <span class="ml-2">
                                            <span class="font-medium">Vertrag:</span>
                                            #{{ row.contractId }}
                                            @if (row.contractName) {
                                                <span class="ml-1 text-500">({{ row.contractName }})</span>
                                            }
                                        </span>
                                    }
                                </div>

                                @if (row.reason) {
                                    <div class="text-sm text-600 mt-1">
                                        <span class="font-medium">Begründung:</span>
                                        <span class="text-color">{{ row.reason }}</span>
                                    </div>
                                }

                                <!-- TM-1000002054: approval right missing — 'Genehmigen' is
                                     disabled and the reason is visible. -->
                                @if (approvalRoleMissing && !row.processed) {
                                    <div class="bd-approval-hint p-2 mt-2 border-round" data-hint="approvalRoleMissing">
                                        <i class="fa-solid fa-circle-exclamation mr-2"></i>
                                        {{ approvalRoleHint }}
                                    </div>
                                }
                                <!-- TM-1000001974 / TM-1000002054: fail-safe hint when the current
                                     user could not be resolved to a real id (AB-11). -->
                                @if (currentUserUnresolved && !row.processed) {
                                    <div class="bd-approval-hint p-2 mt-2 border-round" data-hint="currentUserUnresolved">
                                        <i class="fa-solid fa-circle-exclamation mr-2"></i>
                                        Benutzer konnte nicht ermittelt werden — Freigabe nicht möglich.
                                    </div>
                                }
                            </div>

                            <!-- Right: Action Buttons -->
                            @if (!row.processed) {
                                <div class="flex flex-column gap-2">
                                    <!-- TM-1000002054: 'Genehmigen' only with the approval right
                                         (spinner while it is read) and a resolved user; own
                                         submissions are allowed. -->
                                    <p-button label="Genehmigen"
                                              icon="fa-light fa-check"
                                              severity="success"
                                              [loading]="row.processing || permissionPending"
                                              [disabled]="isApproveBlocked(row)"
                                              (onClick)="approve(row)">
                                    </p-button>
                                    <!-- TM-1000002054 (USER Q3): no self-lock on 'Ablehnen'. -->
                                    <p-button label="Ablehnen"
                                              icon="fa-light fa-xmark"
                                              severity="danger"
                                              [outlined]="true"
                                              [disabled]="row.processing"
                                              (onClick)="toggleRejectForm(row)">
                                    </p-button>
                                </div>
                            }
                        </div>

                        <!-- Reject form -->
                        @if (row.showRejectForm && !row.processed) {
                            <div class="surface-50 border-round p-3 mt-3">
                                <label class="block text-sm font-medium mb-1">
                                    Ablehnungsgrund <span class="text-red-500">*</span>
                                </label>
                                <textarea pInputTextarea
                                          [(ngModel)]="row.rejectionReason"
                                          rows="3"
                                          placeholder="Bitte Ablehnungsgrund eingeben…"
                                          class="w-full"></textarea>
                                <div class="flex justify-content-end gap-2 mt-2">
                                    <p-button label="Abbrechen"
                                              icon="fa-light fa-xmark"
                                              severity="secondary"
                                              [outlined]="true"
                                              size="small"
                                              (onClick)="toggleRejectForm(row)">
                                    </p-button>
                                    <p-button label="Ablehnung bestätigen"
                                              icon="fa-light fa-check"
                                              severity="danger"
                                              size="small"
                                              [loading]="row.processing"
                                              (onClick)="confirmReject(row)">
                                    </p-button>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        }
    </div>
}
